import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Ruler, Plus, Edit, Trash2, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

export interface OpcionTamano {
  id: string;
  nombre: string;
  dimensiones: {
    ancho: number;
    alto: number;
    unidad: string;
  };
  orientacion: 'horizontal' | 'vertical';
  precio: number;
}

interface EditorTamanosProps {
  titulo: string;
  descripcion: string;
  icono?: React.ReactNode;
  opciones: OpcionTamano[];
  onChange: (opciones: OpcionTamano[]) => void;
  mostrarComparacion?: boolean;
  opcionesReferencia?: OpcionTamano[];
}

const TAMANO_VACIO: OpcionTamano = {
  id: '',
  nombre: '',
  dimensiones: { ancho: 0, alto: 0, unidad: 'cm' },
  orientacion: 'horizontal',
  precio: 0,
};

export const EditorTamanos = ({
  titulo,
  descripcion,
  icono,
  opciones,
  onChange,
  mostrarComparacion = false,
  opcionesReferencia = [],
}: EditorTamanosProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editandoIndex, setEditandoIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<OpcionTamano>(TAMANO_VACIO);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleAgregar = () => {
    setEditandoIndex(null);
    setFormData(TAMANO_VACIO);
    setErrors({});
    setIsDialogOpen(true);
  };

  const handleEditar = (index: number) => {
    setEditandoIndex(index);
    setFormData({ ...opciones[index] });
    setErrors({});
    setIsDialogOpen(true);
  };

  const handleEliminar = (index: number) => {
    const nuevasOpciones = opciones.filter((_, i) => i !== index);
    onChange(nuevasOpciones);
  };

  const validarFormulario = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.id.trim()) {
      newErrors.id = 'El ID es requerido';
    } else if (!/^[a-z0-9-]+$/.test(formData.id)) {
      newErrors.id = 'Solo letras minúsculas, números y guiones';
    } else {
      // Verificar que el ID no exista (excepto si estamos editando el mismo)
      const idExiste = opciones.some((op, idx) =>
        op.id === formData.id && idx !== editandoIndex
      );
      if (idExiste) {
        newErrors.id = 'Este ID ya existe';
      }
    }

    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (formData.dimensiones.ancho <= 0) {
      newErrors.ancho = 'El ancho debe ser mayor a 0';
    }

    if (formData.dimensiones.alto <= 0) {
      newErrors.alto = 'El alto debe ser mayor a 0';
    }

    if (formData.precio <= 0) {
      newErrors.precio = 'El precio debe ser mayor a 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleGuardar = () => {
    if (!validarFormulario()) return;

    let nuevasOpciones: OpcionTamano[];
    if (editandoIndex !== null) {
      // Editar existente
      nuevasOpciones = opciones.map((op, idx) =>
        idx === editandoIndex ? formData : op
      );
    } else {
      // Agregar nuevo
      nuevasOpciones = [...opciones, formData];
    }

    onChange(nuevasOpciones);
    setIsDialogOpen(false);
  };

  const calcularCargo = (precio: number, idTamano: string): number | null => {
    if (!mostrarComparacion || !opcionesReferencia.length) return null;
    const referencia = opcionesReferencia.find(op => op.id === idTamano);
    if (!referencia) return null;
    return precio - referencia.precio;
  };

  return (
    <Card className="border-2">
      <CardHeader className="bg-gradient-to-br from-brown-light/5 to-brown-light/10">
        <CardTitle className="flex items-center gap-2">
          {icono || <Ruler className="h-5 w-5" />}
          <div className="flex-1">
            <div className="font-bold">{titulo}</div>
            <div className="text-sm font-normal text-muted-foreground mt-1">
              {descripcion}
            </div>
          </div>
          <Button type="button" onClick={handleAgregar} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Agregar
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        {opciones.length === 0 ? (
          <div className="text-center py-8 border-2 border-dashed rounded-lg bg-gray-50">
            <Ruler className="h-10 w-10 mx-auto mb-3 text-gray-400" />
            <p className="text-gray-600 font-medium">No hay tamaños configurados</p>
            <p className="text-sm text-gray-500 mt-1">
              Haz clic en "Agregar" para crear el primer tamaño
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {opciones.map((opcion, index) => {
              const cargo = calcularCargo(opcion.precio, opcion.id);
              return (
                <div
                  key={opcion.id}
                  className="flex items-center justify-between p-4 border-2 rounded-lg hover:border-primary/50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-bold text-lg">{opcion.nombre}</h4>
                      <Badge variant="outline" className="font-mono">
                        {opcion.id}
                      </Badge>
                      <span className="text-2xl font-bold text-primary">
                        ${opcion.precio}
                      </span>
                      {cargo !== null && cargo > 0 && (
                        <Badge variant="secondary" className="bg-green-100 text-green-800">
                          +${cargo} vs estándar
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>
                        📐 {opcion.dimensiones.ancho}cm × {opcion.dimensiones.alto}cm
                      </span>
                      <span className="capitalize">
                        🔄 {opcion.orientacion}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      onClick={() => handleEditar(index)}
                      size="sm"
                      variant="outline"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      onClick={() => handleEliminar(index)}
                      size="sm"
                      variant="destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Dialog para agregar/editar */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editandoIndex !== null ? 'Editar' : 'Agregar'} Tamaño
              </DialogTitle>
              <DialogDescription>
                Configura las dimensiones y precio para este tamaño
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {/* ID */}
              <div>
                <Label htmlFor="id">
                  ID del tamaño <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="id"
                  placeholder="ej: mini, grande"
                  value={formData.id}
                  onChange={(e) =>
                    setFormData({ ...formData, id: e.target.value.toLowerCase() })
                  }
                  className={errors.id ? 'border-red-500' : ''}
                />
                {errors.id && (
                  <p className="text-xs text-red-500 mt-1">{errors.id}</p>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Solo letras minúsculas, números y guiones
                </p>
              </div>

              {/* Nombre */}
              <div>
                <Label htmlFor="nombre">
                  Nombre <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="nombre"
                  placeholder="ej: Mini, Grande"
                  value={formData.nombre}
                  onChange={(e) =>
                    setFormData({ ...formData, nombre: e.target.value })
                  }
                  className={errors.nombre ? 'border-red-500' : ''}
                />
                {errors.nombre && (
                  <p className="text-xs text-red-500 mt-1">{errors.nombre}</p>
                )}
              </div>

              {/* Dimensiones */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="ancho">
                    Ancho (cm) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="ancho"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="15"
                    value={formData.dimensiones.ancho || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        dimensiones: {
                          ...formData.dimensiones,
                          ancho: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    className={errors.ancho ? 'border-red-500' : ''}
                  />
                  {errors.ancho && (
                    <p className="text-xs text-red-500 mt-1">{errors.ancho}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="alto">
                    Alto (cm) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="alto"
                    type="number"
                    step="0.1"
                    min="0"
                    placeholder="21"
                    value={formData.dimensiones.alto || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        dimensiones: {
                          ...formData.dimensiones,
                          alto: parseFloat(e.target.value) || 0,
                        },
                      })
                    }
                    className={errors.alto ? 'border-red-500' : ''}
                  />
                  {errors.alto && (
                    <p className="text-xs text-red-500 mt-1">{errors.alto}</p>
                  )}
                </div>
              </div>

              {/* Orientación */}
              <div>
                <Label htmlFor="orientacion">Orientación</Label>
                <Select
                  value={formData.orientacion}
                  onValueChange={(value: 'horizontal' | 'vertical') =>
                    setFormData({ ...formData, orientacion: value })
                  }
                >
                  <SelectTrigger id="orientacion">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="horizontal">Horizontal</SelectItem>
                    <SelectItem value="vertical">Vertical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Precio */}
              <div>
                <Label htmlFor="precio">
                  Precio (USD) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="precio"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="15.00"
                  value={formData.precio || ''}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      precio: parseFloat(e.target.value) || 0,
                    })
                  }
                  className={errors.precio ? 'border-red-500' : ''}
                />
                {errors.precio && (
                  <p className="text-xs text-red-500 mt-1">{errors.precio}</p>
                )}
                {mostrarComparacion && opcionesReferencia.length > 0 && (
                  <Alert className="mt-2 bg-blue-50 border-blue-200">
                    <AlertCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-xs text-blue-800">
                      Este precio debe incluir el cargo de personalización
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="button" onClick={handleGuardar}>
                {editandoIndex !== null ? 'Actualizar' : 'Agregar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
